import { useEffect } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { APPLICANT_ATTRIBUTION_BOTH, LOAN_REQUEST_OFFER_STATUS, LOAN_REQUEST_USAGES } from 'cpool-constants';
import { isNumber, objectKeys } from 'cpool-utils';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LuCalendar } from 'react-icons/lu';

import { OfferFileInput } from './OfferFileInput';
import { useOfferBankOptions } from './useOfferBankOptions';
import { FormCurrencyInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormCurrencyInput';
import { FormDropdown } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormDropdown';
import { FormNumberInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormNumberInput';
import { FormPercentInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormPercentInput';
import { FormTextInput } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormTextInput';
import { useApplicantAttributionOptions } from '~/features/leadDetails/components/Overview/CustomerData/shared/useDropdownOptions';
import useLead from '~/features/leadDetails/hooks/useLead';
import type { SchemaManualOfferForm } from '~/features/leadDetails/schemas/manualOfferForm.schema';

const LABEL_CLASS_NAME = 'h-8 w-36 shrink-0 justify-start';

type Props = {
    /** Editing an existing offer keeps the already uploaded document, so the upload field is hidden. */
    isUpdateForm?: boolean;
};

export const OfferForm: React.FC<Props> = ({ isUpdateForm = false }) => {
    const { t } = useTranslation();
    const { control, setValue, watch } = useFormContext<SchemaManualOfferForm>();
    const bankOptions = useOfferBankOptions();
    const allApplicantAttributionOptions = useApplicantAttributionOptions();

    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const hasSecondApplicant = !!lead?.antragsteller2 && !lead?.coApplicantHidden;
    const applicantAttributionOptions = hasSecondApplicant
        ? allApplicantAttributionOptions
        : allApplicantAttributionOptions.filter(
              (option) => option.value === APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1,
          );

    // With a single applicant, "Kreditnehmer" can only ever be that one applicant - lock the field to it
    useEffect(() => {
        if (!hasSecondApplicant) setValue('applicantAttribution', APPLICANT_ATTRIBUTION_BOTH.ANTRAGSTELLER_1);
    }, [hasSecondApplicant, setValue]);

    const creditAmount = watch('creditAmount');
    const commissionResponseInPercent = watch('commissionResponseInPercent');
    const commissionAmount = watch('commissionAmount');

    const financingPurposeOptions = objectKeys(LOAN_REQUEST_USAGES).map((usage) => ({
        label: t(`OFFER_MANAGEMENT.FINANCING_PURPOSE.${usage}`),
        value: usage,
    }));
    const statusOptions = objectKeys(LOAN_REQUEST_OFFER_STATUS).map((status) => ({
        label: t(`OFFER_MANAGEMENT.STATUS.${status}`),
        value: status,
    }));

    // Keeps both commission representations in sync, whichever the user edited last
    const syncCommissionFromPercent = () => {
        if (!isNumber(commissionResponseInPercent) || !isNumber(creditAmount) || creditAmount === 0) return;
        setValue('commissionAmount', (commissionResponseInPercent / 100) * creditAmount);
    };
    const syncCommissionFromAmount = () => {
        if (!isNumber(commissionAmount) || !isNumber(creditAmount) || creditAmount === 0) return;
        setValue('commissionResponseInPercent', (commissionAmount / creditAmount) * 100);
    };

    return (
        <div className="flex flex-col gap-2.5">
            <FormDropdown
                control={control}
                id="applicantAttribution"
                label={t('OFFER_MANAGEMENT.HEADERS.APPLICANT_ATTRIBUTION')}
                labelClassName={LABEL_CLASS_NAME}
                options={applicantAttributionOptions}
                placeholder={t('SELECT_PLACEHOLDER')}
                disabled={!hasSecondApplicant}
            />
            <FormDropdown
                control={control}
                id="bank"
                label={t('OFFER_MANAGEMENT.HEADERS.BANK')}
                labelClassName={LABEL_CLASS_NAME}
                options={bankOptions}
                placeholder={t('SELECT_PLACEHOLDER')}
            />
            <FormDropdown
                control={control}
                id="financingPurpose"
                label={t('OFFER_MANAGEMENT.HEADERS.FINANCING_PURPOSE')}
                labelClassName={LABEL_CLASS_NAME}
                options={financingPurposeOptions}
                placeholder={t('SELECT_PLACEHOLDER')}
            />
            <FormTextInput
                control={control}
                id="providerApplicationNumber"
                label={t('OFFER_MANAGEMENT.HEADERS.APPLICATION_NUMBER')}
                labelClassName={LABEL_CLASS_NAME}
            />
            <FormCurrencyInput
                control={control}
                id="creditAmount"
                label={t('OFFER_MANAGEMENT.HEADERS.CREDIT_AMOUNT')}
                labelClassName={LABEL_CLASS_NAME}
            />
            <FormNumberInput
                control={control}
                id="runtime"
                label={t('OFFER_MANAGEMENT.HEADERS.RUNTIME')}
                labelClassName={LABEL_CLASS_NAME}
                placeholder={t('OFFER_MANAGEMENT.MANUAL_OFFER.RUNTIME_PLACEHOLDER')}
                measuringUnit={<LuCalendar className="h-4 w-4" />}
            />
            <FormCurrencyInput
                control={control}
                id="rate"
                label={t('OFFER_MANAGEMENT.HEADERS.RATE')}
                labelClassName={LABEL_CLASS_NAME}
            />
            <FormPercentInput
                control={control}
                id="interest"
                label={t('OFFER_MANAGEMENT.HEADERS.INTEREST')}
                labelClassName={LABEL_CLASS_NAME}
            />
            <div onBlur={syncCommissionFromPercent}>
                <FormPercentInput
                    control={control}
                    id="commissionResponseInPercent"
                    label={`${t('OFFER_MANAGEMENT.HEADERS.PROVISION')} (%)`}
                    labelClassName={LABEL_CLASS_NAME}
                />
            </div>
            <div onBlur={syncCommissionFromAmount}>
                <FormCurrencyInput
                    control={control}
                    id="commissionAmount"
                    label={`${t('OFFER_MANAGEMENT.HEADERS.PROVISION')} (€)`}
                    labelClassName={LABEL_CLASS_NAME}
                />
            </div>
            <FormDropdown
                control={control}
                id="status"
                label={t('OFFER_MANAGEMENT.HEADERS.STATUS')}
                labelClassName={LABEL_CLASS_NAME}
                options={statusOptions}
                placeholder={t('SELECT_PLACEHOLDER')}
            />
            {!isUpdateForm && <OfferFileInput labelClassName={LABEL_CLASS_NAME} />}
        </div>
    );
};
